import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse, isAxiosError } from 'axios';
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';

import { httpGET, httpPOST, httpPUT } from '../../services/http.ts';
import { PresignedUrlResponse } from '../../types/file.ts';
import { Project } from '../../types/project.ts';
import {
  ChangePasswordRequest,
  CopyProjectRequest,
  CreditsRequest,
  ImpersonateRequest,
  InvitationRequest,
  LoginRequest,
  Norm,
  RegisterRequest,
  ResetPasswordRequest,
  UpdateUserPasswordRequest,
  UpdateUserRequest,
  User,
  VerifyEmailRequest,
} from '../../types/user.ts';
import { toastReducer } from '../reducers/toast.reducer.ts';
import { userReducer } from '../reducers/user.reducer.ts';
import { history } from '../store.ts';
import { unsubscribeFromWebSocket } from './webSocket.saga.ts';

const {
  login,
  setLoginError,
  register,
  setRegisterErrorMessage,
  logout,
  loadUser,
  refreshUser,
  setUser,
  setUserLoadingComplete,
  resetPassword,
  changePassword,
  loadProfilePicture,
  setProfilePicture,
  updateUser,
  updateHideOnboarding,
  updateUserPassword,
  loadNorms,
  setNorms,
  verifyEmail,
  setVerificationSuccess,
  setVerificationError,
  unsubscribeNewsletter,
  setUnsubscribeSuccess,
  setUnsubscribeError,
  impersonate,
  setImpersonationSuccess,
  setImpersonationError,
  sendInvitation,
  setInvitationSuccess,
  setInvitationError,
  sendCredits,
  setCreditsSuccess,
  setCreditsError,
  copyProject,
  setCopyProjectSuccess,
  setCopyProjectError,
  loadUsers,
  setUsers,
  setUsersError,
} = userReducer.actions;

const { showToast } = toastReducer.actions;

function* loginAction(action: PayloadAction<LoginRequest>) {
  try {
    yield call(httpPOST, '/auth/login', action.payload);

    yield put(setLoginError(false));

    const searchParams = new URLSearchParams(window.location.search);
    const returnUrl = searchParams.get('returnUrl') || '/';

    history.push(returnUrl);
  } catch (_error) {
    yield put(setLoginError(true));
  }
}

function* registerAction(action: PayloadAction<RegisterRequest>) {
  try {
    yield call(httpPOST, '/users/register', action.payload);
    yield put(setRegisterErrorMessage(''));
    window.location.href = '/activation-pending';
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      yield put(setRegisterErrorMessage(error.response?.data));
    }
  }
}

function* logoutAction() {
  yield put(unsubscribeFromWebSocket());
  yield call(httpPOST, '/auth/logout', {});
  window.location.href = '/login';
}

function* loadUserAction() {
  try {
    const { data }: AxiosResponse<User> = yield call(httpGET, `/users/user`);
    yield put(setUser(data));
  } catch (_error) {
    yield put(setUserLoadingComplete());
  }
}

function* resetPasswordAction(action: PayloadAction<ResetPasswordRequest>) {
  yield call(httpPOST, `/reset-password`, action.payload);
}

function* changePasswordAction(action: PayloadAction<ChangePasswordRequest>) {
  yield call(httpPOST, `/reset-password/change`, action.payload);
  history.push('/');
}

function* loadProfilePictureAction(action: PayloadAction<number>) {
  const fileId = action.payload;
  const { data }: AxiosResponse<PresignedUrlResponse> = yield call(httpGET, `/files/${fileId}/presigned-url/download`);
  yield put(setProfilePicture(data.url));
}

function* updateUserAction(action: PayloadAction<UpdateUserRequest>) {
  const { data }: AxiosResponse<User> = yield call(httpPUT, '/users/user', action.payload);
  yield put(setUser(data));
}

function* updateHideOnboardingAction() {
  const payload = { hideOnboardingPopup: true };
  const { data }: AxiosResponse<User> = yield call(httpPUT, '/users/user', payload);
  yield put(setUser(data));
}

function* updateUserPasswordAction(action: PayloadAction<UpdateUserPasswordRequest>) {
  yield call(httpPUT, `/users/user/password`, action.payload);
  yield put(
    showToast({
      message: 'user.toast.passwordChanged',
      type: 'success',
    }),
  );
}

function* loadNormsAction() {
  const { data }: AxiosResponse<Norm[]> = yield call(httpGET, `/norms`);
  const sortedData = [...data].sort((a, b) => a.name.localeCompare(b.name));
  yield put(setNorms(sortedData));
}

function* verifyEmailAction(action: PayloadAction<VerifyEmailRequest>) {
  try {
    yield call(httpPOST, `/verification/verify?token=${action.payload.token}`, {});
    yield put(setVerificationSuccess(true));
    yield put(
      showToast({
        message: 'user.toast.emailVerificationSuccess',
        type: 'success',
      }),
    );
  } catch (error) {
    let errorMessage = 'user.toast.emailVerificationError';
    if (isAxiosError(error) && error.response?.data) {
      errorMessage = error.response.data;
    }
    yield put(setVerificationError(errorMessage));
    yield put(
      showToast({
        message: errorMessage,
        type: 'error',
      }),
    );
  }
}

function* unsubscribeNewsletterAction(action: PayloadAction<{ token: string; unsubscribeReason?: string }>) {
  try {
    const body = action.payload.unsubscribeReason ? { unsubscribeReason: action.payload.unsubscribeReason } : {};
    yield call(httpPOST, `/email-subscriptions/unsubscribe/token/${action.payload.token}`, body);
    yield put(setUnsubscribeSuccess(true));
  } catch (error) {
    let errorMessage = 'Failed to unsubscribe from newsletter';
    if (isAxiosError(error) && error.response?.data) {
      errorMessage = error.response.data;
    }
    yield put(setUnsubscribeError(errorMessage));
  }
}

function* impersonateAction(action: PayloadAction<ImpersonateRequest>) {
  try {
    yield call(httpPOST, '/auth/impersonate', action.payload);

    yield put(setImpersonationSuccess());

    yield put(
      showToast({
        message: 'user.toast.impersonationSuccess',
        type: 'success',
      }),
    );

    window.location.href = '/';
  } catch (error) {
    let errorMessage = 'user.toast.impersonationError';
    if (isAxiosError(error) && error.response?.data) {
      errorMessage = error.response.data;
    }

    yield put(setImpersonationError(errorMessage));

    yield put(
      showToast({
        message: errorMessage,
        type: 'error',
      }),
    );
  }
}

function* sendInvitationAction(action: PayloadAction<InvitationRequest>) {
  try {
    yield call(httpPOST, '/invitations', action.payload);

    yield put(setInvitationSuccess());

    yield put(
      showToast({
        message: 'user.toast.invitationSuccess',
        type: 'success',
      }),
    );
  } catch (error) {
    let errorMessage = 'user.toast.invitationError';
    if (isAxiosError(error) && error.response?.data) {
      errorMessage = error.response.data;
    }

    yield put(setInvitationError(errorMessage));

    yield put(
      showToast({
        message: errorMessage,
        type: 'error',
      }),
    );
  }
}

function* sendCreditsAction(action: PayloadAction<CreditsRequest>) {
  try {
    yield call(httpPOST, '/credits', action.payload);

    yield put(setCreditsSuccess());

    yield put(
      showToast({
        message: 'user.toast.creditsSuccess',
        type: 'success',
      }),
    );
  } catch (error) {
    let errorMessage = 'user.toast.creditsError';
    if (isAxiosError(error) && error.response?.data) {
      errorMessage = error.response.data;
    }

    yield put(setCreditsError(errorMessage));

    yield put(
      showToast({
        message: errorMessage,
        type: 'error',
      }),
    );
  }
}

function* copyProjectAction(action: PayloadAction<CopyProjectRequest>) {
  try {
    const { data } = (yield call(httpPOST, `/projects/${action.payload.projectId}/copy`, {
      targetAccountId: action.payload.targetAccountId,
    })) as AxiosResponse<Project>;

    yield put(setCopyProjectSuccess({ id: data.id, name: data.name }));

    yield put(
      showToast({
        message: 'user.toast.copyProjectSuccess',
        type: 'success',
      }),
    );
  } catch (error) {
    let errorMessage = 'user.toast.copyProjectError';
    if (isAxiosError(error) && typeof error.response?.data === 'string' && error.response.data) {
      errorMessage = error.response.data;
    }

    yield put(setCopyProjectError(errorMessage));

    yield put(
      showToast({
        message: errorMessage,
        type: 'error',
      }),
    );
  }
}

function* loadUsersAction() {
  try {
    const { data }: AxiosResponse<User[]> = yield call(httpGET, `/users`);
    yield put(setUsers(data));
  } catch (error) {
    let errorMessage = 'user.toast.loadUsersError';
    if (isAxiosError(error) && error.response?.data) {
      errorMessage = error.response.data;
    }
    yield put(setUsersError(errorMessage));
    yield put(
      showToast({
        message: errorMessage,
        type: 'error',
      }),
    );
  }
}

export default function* userSaga() {
  yield all([takeEvery(login, loginAction)]);
  yield all([takeEvery(register, registerAction)]);
  yield all([takeEvery(logout, logoutAction)]);
  yield all([takeLatest(loadUser, loadUserAction)]);
  yield all([takeLatest(refreshUser, loadUserAction)]);
  yield all([takeEvery(resetPassword, resetPasswordAction)]);
  yield all([takeEvery(changePassword, changePasswordAction)]);
  yield all([takeLatest(loadProfilePicture, loadProfilePictureAction)]);
  yield all([takeEvery(updateUser, updateUserAction)]);
  yield all([takeEvery(updateHideOnboarding, updateHideOnboardingAction)]);
  yield all([takeEvery(updateUserPassword, updateUserPasswordAction)]);
  yield all([takeLatest(loadNorms, loadNormsAction)]);
  yield all([takeEvery(verifyEmail, verifyEmailAction)]);
  yield all([takeEvery(unsubscribeNewsletter, unsubscribeNewsletterAction)]);
  yield all([takeEvery(impersonate, impersonateAction)]);
  yield all([takeEvery(sendInvitation, sendInvitationAction)]);
  yield all([takeEvery(sendCredits, sendCreditsAction)]);
  yield all([takeEvery(copyProject, copyProjectAction)]);
  yield all([takeLatest(loadUsers, loadUsersAction)]);
}
